import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@material-ui/core'
import { ToggleButtonGroup, ToggleButton } from '@material-ui/lab'
import { makeStyles } from '@material-ui/core/styles'
import { AxiosError } from 'axios'
import React, { useState } from 'react'
import toast from 'react-hot-toast'
import { useMutation } from 'react-query'
import { Pallet } from 'src/api/models'
import { useQueryPaginator } from 'src/components/Table'
import { useForm, BooleanField, NumberField, QuerySelectField, TextareaField } from 'src/forms'
import { setApiErrors } from 'src/forms/errors'
import { skuit } from 'src/services/skuit'
import { handleErrorMessage, Notification } from 'src/components/Notification'
import * as yup from 'yup'

const useStyles = makeStyles(() => ({
  form: {
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
  },
}))

interface PalletProps {
  name: string
  client_uid: string | null //  eslint-disable-line camelcase
  count?: number
}

const palletSchema = yup.object().shape({
  name: yup.string().label('Name'),
  client_uid: yup.string().nullable().label('Client ID'),
})

interface Props {
  open: boolean
  handleClose: () => void
  onSave?: () => void
  pallet?: Pallet
  update?: boolean
}

enum Mode {
  Single,
  Bulk,
}

export const PalletDialog = (props: Props) => {
  const { open, handleClose, onSave, pallet, update } = props
  const classes = useStyles()

  const [mode, setMode] = useState<Mode>(Mode.Single)

  const facilityPaginator = useQueryPaginator('facility-list', skuit.sc.facilities.list)
  const placePaginator = useQueryPaginator('place-list', skuit.sc.places.list, { params: { facilities: pallet?.facility } })
  const clientPaginator = useQueryPaginator('client-list', skuit.sc.clients.list)

  const handleToggle = (event: React.MouseEvent<HTMLElement>, mode: Mode) => {
    setMode(mode)
  }

  const { mutate: createPallet } = useMutation(skuit.sc.pallets.create, {
    onSuccess: (pallets: Pallet[]) => {
      if (pallets.length > 1) {
        toast.success(() => (
          <Notification
            message={`Successfully created ${pallets.length} pallets! Click "View" to see the first pallet.`}
            newObject={pallets[0]}
          />
        ))
      } else {
        toast.success(() => (
          <Notification
            message={`Successfully created ${pallets.length} pallets!`}
            newObject={pallets[0]}
          />
        ))
      }

      onSave?.()
      handleClose()
    },

    onError: (error: AxiosError) => {
      handleErrorMessage(error, 'creat', 'Pallet')
      setApiErrors(formData.setError, error)
    },
  })

  const { mutate: updatePallet } = useMutation(skuit.sc.pallets.patch, {
    onSuccess: (updatedPallet) => {
      toast.success(() => (
        <Notification message={`Successfully updated pallet!`} newObject={updatedPallet} />
      ))

      onSave?.()
      handleClose()
    },
    onError: (error: AxiosError) => {
      handleErrorMessage(error, `${update ? 'updat' : 'creat'}`, 'Pallet')
      setApiErrors(formData.setError, error)
    },
  })

  const onSubmit = (data: PalletProps) => {
    if (update) {
      updatePallet({ guid: pallet?.guid || '', data })
    } else {
      if (mode === Mode.Single) {
        data.count = 1
      } else {
        data.client_uid = null
      }
      createPallet(data)
    }
  }

  const { Form, Field, data: formData } = useForm(palletSchema)

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <Form formProps={{ className: classes.form }} onSubmit={onSubmit}>
        <DialogTitle>{update ? 'Update' : 'Create'} Pallet</DialogTitle>
        <DialogContent>
          {!update && (
            <ToggleButtonGroup exclusive value={mode} onChange={handleToggle}>
              <ToggleButton value={Mode.Single} aria-label="single">
                Single
              </ToggleButton>
              <ToggleButton value={Mode.Bulk} aria-label="bulk">
                Bulk
              </ToggleButton>
            </ToggleButtonGroup>
          )}
          <QuerySelectField
            name="facility"
            label="Facility"
            paginator={facilityPaginator}
            defaultValue={pallet?.facility}
            disabled={update}
            onSelected={(facility) => {
              placePaginator.patchFilters({ facilities: facility.guid})
              formData.setValue('place', undefined)
            }}
          />
          <QuerySelectField
            name="place"
            label="Place"
            paginator={placePaginator}
            defaultValue={pallet?.place}
            disabled={update}
          />
          <QuerySelectField
            name="client"
            label="Client"
            paginator={clientPaginator}
            defaultValue={pallet?.client}
            disabled={update}
          />
          {mode === Mode.Single && (
            <Field
              name="client_uid"
              defaultValue={pallet?.clientID}
              renderField={({ name, value, setValue, error }) => (
                <TextField
                  id={name}
                  name={name}
                  label="Client ID"
                  error={!!error}
                  helperText={error}
                  fullWidth
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setValue(name, e.target.value)
                  }
                  value={value || ''}
                />
              )}
            />
          )}
          <BooleanField
            name="for_shipping"
            label="For Shipping"
            defaultValue={pallet?.forShipping}
          />
          <TextareaField
            name="internal_notes"
            label="Internal Notes"
            defaultValue={pallet?.internalNotes}
          />
          <BooleanField
            name="review_needed"
            label="Review Needed"
            defaultValue={pallet?.reviewNeeded}
          />
          <TextareaField
            name="review_reason"
            label="Review Reason"
            defaultValue={pallet?.reviewReason}
          />
          {!update && mode === Mode.Bulk && (
            <NumberField
              name="count"
              defaultValue={1}
              label="Count"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
          <Button type="submit" color="primary" variant="contained" autoFocus>
            Save
          </Button>
        </DialogActions>
      </Form>
    </Dialog>
  )
}
